import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'
import dayjs from 'dayjs'
import useToastContext from 'hooks/useToastContext'
import { exportReportToExcel } from 'utils/reportExport'

// ═══ CHART.JS IMPORTS ═══
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Line } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
)

// ═══ PAGINATION CONSTANTS ═══
const ITEMS_PER_PAGE = 10;

const ScheduledVsActualReport = ({ selectedEmployees, dateRange, onExportReady }) => {
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const notification = useToastContext()

  // ═══ PAGINATION STATE ═══
  const [currentPage, setCurrentPage] = useState(1)
  
  // ═══ REFS ═══
  const isRegistered = useRef(false);
  const isExportingRef = useRef(false);

  useEffect(() => {
    const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
    const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
    fetchData(start, end, currentPage)
  }, [selectedEmployees, dateRange, currentPage])

  // ═══ RESET PAGE TO 1 WHENEVER FILTER CHANGES ═══
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedEmployees, dateRange])

  const fetchData = async (start, end, page) => {
    setIsLoading(true)
    try {
      const employeeIDs = selectedEmployees.map(e => e.value)
      const payload = {
        startDate: start,
        endDate: end,
        page: page,
        limit: ITEMS_PER_PAGE,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      const result = await apiGet(apiPath.reportScheduledVsActual, payload)
      
      if (result?.data?.success) {
        let finalData = []
        const rawResults = result.data.results
        
        // Handle nested or flat response
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          finalData = rawResults.results
          setTotalRecords(rawResults.total || 0)
        } else if (Array.isArray(rawResults)) {
          finalData = rawResults
          setTotalRecords(rawResults.length) // Fallback
        }
        setData(finalData || [])
      } else {
        setData([])
        setTotalRecords(0)
      }
    } catch (error) {
      console.error('Error fetching scheduled vs actual report:', error)
      notification.error('Error fetching report data')
      setData([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
    }
  }

  // ═══ HANDLE EXPORT ═══
  const handleExport = useCallback(async () => {
    // Prevent multiple concurrent exports
    if (isExportingRef.current) {
      return;
    }
    
    isExportingRef.current = true;
    setIsExporting(true);
    
    try {
      const start = dateRange.startDate || dayjs().startOf('month').format('YYYY-MM-DD')
      const end = dateRange.endDate || dayjs().endOf('month').format('YYYY-MM-DD')
      const employeeIDs = selectedEmployees.map(e => e.value)
      
      const payload = {
        startDate: start,
        endDate: end,
        export: true,
        ...(employeeIDs.length > 0 && { employeeIDs: JSON.stringify(employeeIDs) })
      }
      
      const result = await apiGet(apiPath.reportScheduledVsActual, payload)
      
      if (result?.data?.success) {
        let allData = []
        const rawResults = result.data.results
        if (rawResults && typeof rawResults === 'object' && Array.isArray(rawResults.results)) {
          allData = rawResults.results
        } else if (Array.isArray(rawResults)) {
          allData = rawResults
        }
        
        if (allData.length > 0) {
          exportReportToExcel(allData, 'Scheduled vs Actual', 'scheduled_vs_actual')
        } else {
          notification.error('No data available to export')
        }
      } else {
        notification.error('Failed to fetch all data for export')
      }
    } catch (error) {
      console.error('Export failed:', error)
      notification.error('Export failed')
    } finally {
      setIsExporting(false)
      isExportingRef.current = false;
    }
  }, [dateRange, selectedEmployees, notification])

  // ═══ REGISTER EXPORT FUNCTION WITH PARENT ═══
  useEffect(() => {
    // Only register, don't call the export function
    if (onExportReady && !isRegistered.current && data.length > 0) {
      onExportReady(handleExport);
      isRegistered.current = true;
    }
    
    // Cleanup when component unmounts
    return () => {
      if (onExportReady && isRegistered.current) {
        onExportReady(null);
        isRegistered.current = false;
      }
    }
  }, [handleExport, onExportReady, data]);

  // ═══ PAGINATION CALCULATIONS ═══
  const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE)

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  // ═══ CHART 1 DATA: EMPLOYEE AGGREGATION (GROUPED BAR) ═══
  // 🔥 FIX: useMemo must be declared BEFORE any early returns!
  const employeeAggregation = useMemo(() => {
    if (data.length === 0) return [];
    const map = {};
    data.forEach(row => {
      const key = row.employeeId;
      if (!map[key]) {
        map[key] = {
          name: row.employeeName,
          scheduled: 0,
          actual: 0
        };
      }
      map[key].scheduled += row.scheduledHours || 0;
      map[key].actual += row.actualHours || 0;
    });
    return Object.values(map);
  }, [data]);

  // ═══ CHART 2 DATA: DATE AGGREGATION (LINE CHART) ═══
  // 🔥 FIX: useMemo must be declared BEFORE any early returns!
  const dateAggregation = useMemo(() => {
    if (data.length === 0) return [];
    const map = {};
    data.forEach(row => {
      const key = row.date;
      if (!map[key]) {
        map[key] = {
          scheduled: 0,
          actual: 0
        };
      }
      map[key].scheduled += row.scheduledHours || 0;
      map[key].actual += row.actualHours || 0;
    });
    // Sort by date
    return Object.keys(map).sort().map(date => ({
      date,
      scheduled: map[date].scheduled,
      actual: map[date].actual
    }));
  }, [data]);

  // ═══ EARLY RETURNS ═══
  // These MUST be declared BELOW all React Hooks (useState, useEffect, useMemo, useCallback)
  if (isLoading) return null;
  if (!data.length && !isLoading) return <EmptyState />

  // Calculate Totals for Summary Cards
  const totalScheduled = data.reduce((sum, row) => sum + (row.scheduledHours || 0), 0)
  const totalActual = data.reduce((sum, row) => sum + (row.actualHours || 0), 0)
  const totalVariance = totalActual - totalScheduled

  // ═══ CHART 1: SCHEDULED vs ACTUAL (GROUPED BAR) ═══
  const barChartOptions = {
    indexAxis: 'y', // Horizontal
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.x.toFixed(1)}h`
        }
      }
    },
    scales: {
      x: { 
        grid: { display: false }, 
        ticks: { callback: (val) => val + 'h', font: { size: 10 } } 
      },
      y: { 
        grid: { display: false }, 
        ticks: { font: { size: 10 } } 
      }
    }
  };

  const barChartData = {
    labels: employeeAggregation.map(item => item.name),
    datasets: [
      {
        label: 'Scheduled Hours',
        data: employeeAggregation.map(item => item.scheduled),
        backgroundColor: '#2563EB', // Blue (Matches UI)
        borderColor: '#1E40AF',
        borderWidth: 1,
        borderRadius: 3,
      },
      {
        label: 'Actual Hours',
        data: employeeAggregation.map(item => item.actual),
        backgroundColor: '#34D399', // Green (Matches UI)
        borderColor: '#10B981',
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  };

  // ═══ CHART 2: DAILY HOURS TREND (LINE CHART) ═══
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}h`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: '#f3f4f6' },
        ticks: { callback: (val) => val + 'h', font: { size: 10 } }
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } }
      }
    }
  };

  const lineChartData = {
    labels: dateAggregation.map(item => dayjs(item.date).format('MMM DD')),
    datasets: [
      {
        label: 'Scheduled Hours',
        data: dateAggregation.map(item => item.scheduled),
        borderColor: '#2563EB',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#2563EB',
        pointRadius: 4
      },
      {
        label: 'Actual Hours',
        data: dateAggregation.map(item => item.actual),
        borderColor: '#34D399',
        backgroundColor: 'rgba(52, 211, 153, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#34D399',
        pointRadius: 4
      }
    ]
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className='grid grid-cols-3 gap-4 mb-6'>
        <SummaryCard label='Total Scheduled' value={`${totalScheduled.toFixed(1)}h`} color='blue' />
        <SummaryCard label='Total Actual' value={`${totalActual.toFixed(1)}h`} color='green' />
        <SummaryCard
          label='Total Variance'
          value={totalVariance >= 0 ? `+${totalVariance.toFixed(1)}h` : `${totalVariance.toFixed(1)}h`}
          color={totalVariance > 0.5 ? 'orange' : totalVariance < -0.5 ? 'red' : 'green'}
        />
      </div>

      {/* ═══ CHARTS SECTION ═══ */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
        
        {/* Chart 1: Employee Aggregation Bar Chart */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Total Hours by Employee</h3>
          <div className='h-64 w-full'>
            <Bar options={barChartOptions} data={barChartData} />
          </div>
        </div>

        {/* Chart 2: Daily Timeline Line Chart */}
        <div className='bg-white border border-[#E1E6EE] rounded-lg p-4 shadow-sm'>
          <h3 className='text-sm font-semibold text-gray-700 mb-3'>Daily Hours Trend</h3>
          <div className='h-64 w-full'>
            <Line options={lineChartOptions} data={lineChartData} />
          </div>
        </div>
      </div>

      {/* ═══ TABLE ═══ */}
      <div className='overflow-x-auto rounded-lg border border-[#E1E6EE]'>
        <table className='w-full text-xs text-left'>
          <thead className='text-xs text-gray-600 bg-[#EBEFF8]'>
            <tr>
              <th className='py-3 px-4 font-medium'>Sr no.</th>
              <th className='py-3 px-4 font-medium'>Date</th>
              <th className='py-3 px-4 font-medium'>Employee</th>
              <th className='py-3 px-4 font-medium'>Employee ID</th>
              <th className='py-3 px-4 font-medium'>Shift</th>
              <th className='py-3 px-4 font-medium'>Scheduled Hrs</th>
              <th className='py-3 px-4 font-medium'>Actual Hrs</th>
              <th className='py-3 px-4 font-medium'>Variance</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const variance = (row.actualHours || 0) - (row.scheduledHours || 0)
              return (
                <tr key={i} className='bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                  <td className='py-3 px-4 font-medium text-gray-800'>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                  <td className='py-3 px-4 text-gray-600'>
                    {dayjs(row.date).format('MMM DD, YYYY')}
                  </td>
                  <td className='py-3 px-4 font-medium text-gray-800'>{row.employeeName}</td>
                  <td className='py-3 px-4 text-gray-600'>{row.employeeId}</td>
                  <td className='py-3 px-4'>
                    <span className='inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium'>
                      {row.shiftName || 'Unassigned'}
                    </span>
                  </td>
                  <td className='py-3 px-4 text-gray-600'>{(row.scheduledHours || 0).toFixed(1)}h</td>
                  <td className='py-3 px-4 text-gray-600'>{(row.actualHours || 0).toFixed(1)}h</td>
                  <td className={`py-3 px-4 font-medium ${variance > 0.5 ? 'text-orange-500' : variance < -0.5 ? 'text-red-500' : 'text-green-600'}`}>
                    {variance > 0.5 ? `+${variance.toFixed(2)}h` : variance < -0.5 ? `${variance.toFixed(2)}h` : 'On target'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ═══ PAGINATION FOOTER ═══ */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between mt-4 pt-2 me-8 border-t border-gray-100'>
          <div className='text-sm text-gray-500'>
            Total <span className='font-medium text-gray-700'>{totalRecords}</span> Records
          </div>
          <div className='flex items-center gap-1'>
            <button
              onClick={() => currentPage === 1 ? null : handlePageChange(currentPage - 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors border ${
                currentPage === 1 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Prev
            </button>
            
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-8 h-8 rounded text-sm font-medium flex items-center border justify-center transition-colors ${
                    currentPage === pageNum
                      ? 'bg-[#1E40AF] text-white'
                      : 'text-gray-600 bg-transparent hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
            
            <button
              onClick={() => currentPage === totalPages ? null : handlePageChange(currentPage + 1)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                currentPage === totalPages 
                  ? 'text-gray-300 cursor-not-allowed bg-transparent' 
                  : 'text-gray-600 bg-transparent hover:bg-gray-100'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ScheduledVsActualReport

// ─── Shared UI helpers ─────────────────────────────────────────────────────────

export const SummaryCard = ({ label, value, color }) => {
  const colorMap = {
    blue: 'text-[#2563EB] bg-blue-50 border-blue-100',
    green: 'text-green-600 bg-green-50 border-green-100',
    orange: 'text-orange-500 bg-orange-50 border-orange-100',
    red: 'text-red-500 bg-red-50 border-red-100',
    purple: 'text-purple-600 bg-purple-50 border-purple-100'
  }
  return (
    <div className={`rounded-lg border p-4 ${colorMap[color] || colorMap.blue}`}>
      <p className='text-xs text-gray-500 mb-1'>{label}</p>
      <p className={`text-2xl font-bold ${colorMap[color]?.split(' ')[0]}`}>{value}</p>
    </div>
  )
}

export const LoadingState = () => (
  <div className='flex items-center justify-center py-16 text-gray-400'>
    <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB]' />
    <span className='ml-3 text-sm'>Loading report...</span>
  </div>
)

export const EmptyState = () => (
  <div className='flex flex-col items-center justify-center py-16 text-gray-400'>
    <p className='text-sm font-medium'>No data found for the selected filters</p>
    <p className='text-xs mt-1'>Try adjusting the date range or employee selection</p>
  </div>
)